// changelog.tsx
import { useState, useEffect, useMemo } from 'react';
import { useApiData } from '../../contexts/apiService';
import { ChangelogItem } from '../../contexts/tableDefinitions';
import SidePanel from './sidePanel';
import './changelog.css';
import LoaderWheel from '../../components/common/LoaderWheel';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';
import { useDataManager } from "../../contexts/dataManager";
import SQLTable, { TableColumnConfig } from "../../components/sql_table/tableMain";
import MonthNavigation from '../../components/sql_table/table_tools/monthNavigation/monthNavigation';
import ComparisonPanel from './ComparisonPanel';

const ChangelogPage = () => {
    const { apiData, isLoading, handleGetChangeLog } = useApiData();
    const [showSidePanel, setShowSidePanel] = useState(false);
    const [sidePanelData, setSidePanelData] = useState<any>(null);

    const [ridValue, setRidValue] = useState('');
    const [ridError, setRidError] = useState('');
    const [showRIDModal, setShowRIDModal] = useState(false);
    const [ridSearchResults, setRidSearchResults] = useState<ChangelogItem[]>([]);
    const [isRidSearchLoading, setIsRidSearchLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState<string>('');
    const { userRole = 'viewer' } = useUserAccess();
    const { requestTableData, isTableLoading, hasTableData } = useDataManager();

    useEffect(() => {
        const currentDate = new Date();
        const monthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        setSelectedMonth(monthYear);
        
        // Backend expects "December 2024" format
        const backendFormat = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });
        if (!hasTableData('changelog', backendFormat)) {
            requestTableData("changelog", backendFormat);
        }
    }, []);

    // Handle month change from MonthNavigation component
    const handleMonthChange = (monthYear: string) => {
        // MonthNavigation sends "December 2024" format
        // Convert to "YYYY-MM" for internal state
        const date = new Date(monthYear);
        const formattedMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        setSelectedMonth(formattedMonth);

        // Request data using the original "December 2024" format that backend expects
        if (!hasTableData('changelog', monthYear)) {
            requestTableData("changelog", monthYear);
        }
    };

    // Add row_data_display and filter by selected month/year
    const filteredByMonth = useMemo(() => {
        if (!apiData?.changelog) return [];

        // If no month selected yet, return empty array to avoid showing all data
        if (!selectedMonth) return [];

        // Parse selected month (format: "YYYY-MM") and filter
        const [yearStr, monthStr] = selectedMonth.split('-');
        if (!monthStr || !yearStr) return [];

        // Filter and add display field in one pass for better performance
        return apiData.changelog
            .filter(row => {
                const date = new Date(row.changed_at);
                const rowMonth = String(date.getMonth() + 1).padStart(2, '0');
                const rowYear = date.getFullYear().toString();
                return rowMonth === monthStr && rowYear === yearStr;
            })
            .map(row => ({
                ...row,
                row_data_display: row.row_data ? 'Show Data' : '-'
            }));
    }, [apiData?.changelog, selectedMonth]);

    // Sort by date (most recent first) - memoize sorted array separately
    const sortedData = useMemo(() => {
        return [...filteredByMonth].sort((a, b) =>
            new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
        );
    }, [filteredByMonth]);

    const handleShowData = (rowData: ChangelogItem) => {
        if (rowData.row_data) {
            setSidePanelData(rowData.row_data);
            setShowSidePanel(true);
        }
    };

    const handleRIDSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!ridValue.trim()) {
            setRidError('Please enter a valid RID.');
            return;
        }
        setRidError('');
        setIsRidSearchLoading(true);
        
        try {
            const data = await handleGetChangeLog('changelog', ridValue.trim());

            const filteredResults: ChangelogItem[] = (data ?? [])
                .sort((a: ChangelogItem, b: ChangelogItem) =>
                    new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
                )
                .map((row: ChangelogItem) => ({
                    ...row,
                    row_data_display: row.row_data ? 'Show Data' : '-'
                }));

            setRidSearchResults(filteredResults);
            setShowRIDModal(true);
            setIsRidSearchLoading(false);
        } catch (error) {
            console.error('Error fetching changelog by RID:', error);
            setRidError('Failed to fetch changelog. Please try again.');
            setIsRidSearchLoading(false);
        }
    };

    // Use the RID search results from state instead of filtering all changelog data
    const ridModalData = useMemo(() => {
        return showRIDModal ? ridSearchResults : [];
    }, [showRIDModal, ridSearchResults]);

    // Column configuration for SQLTable
    const columnConfigs: TableColumnConfig[] = [
        { colName: 'id', colType: 'text', colHeader: 'ID' },
        { colName: 'table_name', colType: 'text', colHeader: 'Table Name' },
        { colName: 'changed_by', colType: 'text', colHeader: 'User' },
        { colName: 'changed_at', colType: 'datetime', colHeader: 'Date' },
        {
            colName: 'action',
            colType: 'text',
            colHeader: 'Action',
            cellColor: [
                { condition: (row) => row.action === 'delete', color: '#ffcccc' },
                { condition: (row) => row.action === 'create', color: '#ccffcc' },
                { condition: (row) => row.action === 'edit', color: '#ffffcc' },
            ]
        },
        { colName: 'record_id', colType: 'text', colHeader: 'Row ID' },
        { colName: 'record_rid', colType: 'text', colHeader: 'Record RID' },
        { colName: 'edited_col', colType: 'text', colHeader: 'Column Edited' },
        { colName: 'old_value', colType: 'text', colHeader: 'Old Value' },
        { colName: 'new_value', colType: 'text', colHeader: 'New Value' },
        { colName: 'row_data_display', colType: 'text', colHeader: 'Row Data' },
    ];

    // RID modal columns
    const ridModalColumns = [
        { id: 'table_name', header: 'Table Name', accessor: 'table_name' },
        { id: 'changed_by', header: 'User', accessor: 'changed_by' },
        { id: 'changed_at', header: 'Date', accessor: 'changed_at' },
        { id: 'action', header: 'Action', accessor: 'action' },
        { id: 'record_id', header: 'Row ID', accessor: 'record_id' },
        { id: 'record_rid', header: 'Record RID', accessor: 'record_rid' },
        { id: 'edited_col', header: 'Column Edited', accessor: 'edited_col' },
        { id: 'old_value', header: 'Old Value', accessor: 'old_value' },
        { id: 'new_value', header: 'New Value', accessor: 'new_value' },
        { id: 'row_data', header: 'Row Data', accessor: 'row_data' },
    ];

    const SCHEDULE_ADMIN_LEVEL = USER_ROLE_LEVEL['schedule_admin'];
    const showTable =  Number(USER_ROLE_LEVEL[userRole]) >= SCHEDULE_ADMIN_LEVEL;
    const showComparison = Number(USER_ROLE_LEVEL[userRole]) >= USER_ROLE_LEVEL['admin'];

    if (!showTable) {
        return <p>Not Allowed</p>;
    }

    return (
        <div className='changelog-mainContainer'>
            {/* Place the form above the table container */}
            <form onSubmit={handleRIDSubmit} className="changelog-search-form">
                <label htmlFor="rid-input">Search by RID:&nbsp;</label>
                <input
                    id="rid-input"
                    type="text"
                    value={ridValue}
                    onChange={e => setRidValue(e.target.value)}
                    placeholder="Enter Record RID"
                    disabled={isRidSearchLoading}
                />
                <button type="submit" disabled={isRidSearchLoading}>
                    {isRidSearchLoading ? 'Searching...' : 'Search'}
                </button>
                {ridError && <span style={{ color: 'red', marginLeft: 8 }}>{ridError}</span>}
            </form>

            {/* Month selector - using existing MonthNavigation component */}
            <div className="changelog-controls">
                <MonthNavigation
                    tableName="changelog"
                    onMonthChange={handleMonthChange}
                />
            </div>

            {/* DB Comparison button - admin+ only */}
            {showComparison && <ComparisonPanel
                tables={['main_table', 'schedule_table', 'bottoms_schedule_table', 'ngl_schedule_table', 'crude_schedule_pickup', 'crude_schedule_delivery']}
                monthTrackedTables={['bottoms_schedule_table', 'ngl_schedule_table', 'schedule_table']}
                subTableConfigs={[
                    { tableName: 'crude_schedule_pickup', parentTable: 'schedule_table', parentField: 'pickup_data' },
                    { tableName: 'crude_schedule_delivery', parentTable: 'schedule_table', parentField: 'delivery_data' },
                ]}
                buttonLabel="Run DB Comparison"
            />}


            {/* Main table */}
            <div className='changelog-tableContainer'>
                <SQLTable
                    tableName="changelog"
                    tableData={sortedData}
                    idColumn="id"
                    columnConfigs={columnConfigs}
                    showHeader={true}
                    tableTools={true}
                    enableFilters={true}
                    downloadable={true}
                    refreshable={true}
                    selectableRows={false}
                    deletableRows={false}
                    userRoleLevel={USER_ROLE_LEVEL[userRole] || 1}
                    showTable={showTable}
                    onRowClick={handleShowData}
                    pageSize={100}
                />
            </div>

            {/* Side panel for row data */}
            {showSidePanel && sidePanelData && (
                <SidePanel data={sidePanelData} onClose={() => setShowSidePanel(false)} />
            )}

            {/* RID Search Modal */}
            {showRIDModal && (
                <div className="changelog-modal-overlay">
                    <div className="changelog-modal-content">
                        <h3>Changelog Results for RID: {ridValue}</h3>
                        <button 
                            className="changelog-modal-close-btn" 
                            onClick={() => {
                                setShowRIDModal(false);
                                setRidSearchResults([]);
                            }}
                        >
                            Close
                        </button>
                        {ridModalData.length > 0 ? (
                            <table className="changelog-modal-table">
                                <thead>
                                    <tr>
                                        {ridModalColumns.map(col => (
                                            <th key={col.id}>{col.header}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {ridModalData.map((row: any, idx: number) => (
                                        <tr key={idx}>
                                            {ridModalColumns.map(col => (
                                                <td key={col.id}>
                                                    {col.id === 'row_data'
                                                        ? <pre style={{ margin: 0, fontSize: '0.95em' }}>{JSON.stringify(row[col.accessor], null, 2)}</pre>
                                                        : row[col.accessor]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div>No results found for this RID.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChangelogPage;